import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Board } from '@/chess/board';
import {
  FixedNav,
  Caption,
  Button,
  TurnIndicator,
  ErrorMessageWithDebug,
  MODULE_LABEL_FR,
} from '@/ui';
import type { Outcome } from '@/api/outcome';
import { useDueCards, useReviewMutation } from './hooks';
import type { DueCard } from './types';

/**
 * Revision Cards — drill flashcard.
 *
 * URL : `/revision-cards/drill/:cardId`. La carte est lue depuis
 * `useDueCards()` puis filtrée côté client par id (pas d'endpoint single
 * — on évite de multiplier les contrats backend en V1).
 *
 * Modèle UX (plan §2) : Phase 1 réflexion → bouton « Voir la solution » →
 * Phase 2 solution révélée (texte « Bon coup : e4e5 » — pas de flèche
 * Chessground en V1 par mesure de simplicité, cf. consigne sub-agent).
 * Puis 3 boutons d'auto-évaluation → mapping `Outcome` → mutation
 * `postReview` → toast bref + navigate back to `/revision-cards`.
 *
 * Conventions :
 *  - `time_spent_ms` mesuré depuis le mount via `performance.now()`.
 *  - Wording FR neutre (CLAUDE.md §5, DESIGN.md §6).
 *  - Émojis tolérés sur les 3 boutons (rendu UI explicite à Léon).
 */

type Phase = 'thinking' | 'revealed';

interface OutcomeOption {
  outcome: Outcome;
  label: string;
  accent: 'volt' | 'amber' | 'coral';
  testId: string;
}

const OUTCOME_OPTIONS: readonly OutcomeOption[] = [
  {
    outcome: 'success_clean',
    label: '✅ Réussi rapide',
    accent: 'volt',
    testId: 'outcome-success-clean',
  },
  {
    outcome: 'success_slow',
    label: '🟡 Trouvé difficile',
    accent: 'amber',
    testId: 'outcome-success-slow',
  },
  {
    outcome: 'fail_not_understood',
    label: '❌ Échec / pas vu',
    accent: 'coral',
    testId: 'outcome-fail',
  },
] as const;

/**
 * Formate un timestamp ISO en distance relative à maintenant
 * (« il y a 1 jour », « il y a 3 h »). FR, neutre, simple — pas
 * d'`Intl.RelativeTimeFormat` (overkill pour 4 buckets).
 */
function formatRelativeFromNow(iso: string | null): string | null {
  if (iso === null) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `il y a ${diffD} j`;
  const diffMo = Math.floor(diffD / 30);
  return `il y a ${diffMo} mois`;
}

export function RevisionDrillPage(): JSX.Element {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const dueQ = useDueCards();
  const mutation = useReviewMutation();

  const [phase, setPhase] = useState<Phase>('thinking');
  // Mesure du temps : `performance.now()` est monotone, immune au changement
  // d'horloge système. Le ref est figé au mount (pas re-créé à chaque render).
  const startedAtRef = useRef<number>(performance.now());

  // Cf. `noUncheckedIndexedAccess` : `find()` peut retourner undefined si
  // l'id est invalide OU si la carte est déjà archivée (filtrée backend).
  const card: DueCard | null = useMemo(() => {
    if (!dueQ.data || cardId === undefined) return null;
    const idNum = Number.parseInt(cardId, 10);
    if (Number.isNaN(idNum)) return null;
    return dueQ.data.cards.find((c) => c.id === idNum) ?? null;
  }, [dueQ.data, cardId]);

  const chips = useMemo(
    () => [
      { label: 'Mode', value: 'Révision', color: 'var(--paper-50)' },
    ],
    [],
  );

  function handleOutcome(outcome: Outcome): void {
    if (card === null) return;
    if (mutation.isPending) return;
    const elapsed = Math.round(performance.now() - startedAtRef.current);
    mutation.mutate(
      { cardId: card.id, outcome, timeSpentMs: elapsed },
      {
        onSuccess: () => {
          // Toast très bref via état local + redirect — on évite d'ajouter
          // une lib toast pour 1 cas d'usage. Navigation suffit comme feedback.
          navigate('/revision-cards');
        },
      },
    );
  }

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[960px] px-6 py-6 flex flex-col gap-5">
        <header className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate('/revision-cards')}
            className="self-start text-body-sm text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Cartes à réviser
          </button>
        </header>

        {dueQ.isPending && (
          <p data-testid="loading" className="text-ink-300 italic">
            Chargement de la carte…
          </p>
        )}
        {dueQ.isError && (
          <ErrorMessageWithDebug
            className="text-coral-500"
            message="Erreur de chargement. Réessaie."
            debug="GET /api/v1/training/cards/due échoué"
          />
        )}
        {dueQ.data && card === null && (
          <section
            data-testid="not-found"
            className="glass p-6 rounded-xl flex flex-col gap-3 max-w-xl"
          >
            <Caption color="coral">Carte introuvable</Caption>
            <p className="text-body-sm text-paper-50">
              Cette carte n'est plus due (peut-être déjà révisée). Retour à
              la queue.
            </p>
            <div>
              <Button variant="primary" onClick={() => navigate('/revision-cards')}>
                Retour
              </Button>
            </div>
          </section>
        )}

        {card !== null && <DrillContent card={card} phase={phase} setPhase={setPhase} onOutcome={handleOutcome} isSubmitting={mutation.isPending} />}
      </main>
    </>
  );
}

interface DrillContentProps {
  card: DueCard;
  phase: Phase;
  setPhase: (p: Phase) => void;
  onOutcome: (o: Outcome) => void;
  isSubmitting: boolean;
}

function DrillContent({
  card,
  phase,
  setPhase,
  onOutcome,
  isSubmitting,
}: DrillContentProps): JSX.Element {
  const moduleLabel = MODULE_LABEL_FR[card.module] ?? card.module;
  const failureRel = formatRelativeFromNow(card.last_failure_at);

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,360px)] gap-6 items-start">
      {/* LEFT — board */}
      <div className="flex flex-col gap-3">
        <TurnIndicator
          state="user-turn"
          userLabel={card.side_to_move === 'white' ? 'Trait aux Blancs' : 'Trait aux Noirs'}
        />
        <div data-testid="drill-board" style={{ maxWidth: 480 }}>
          <Board
            fen={card.fen}
            orientation={card.side_to_move}
            interactive={false}
          />
        </div>
      </div>

      {/* RIGHT — contexte + actions */}
      <aside className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 p-4 rounded-xl border border-ink-700 bg-ink-800">
          <Caption>Contexte</Caption>
          <h2 className="font-display text-heading-3 text-paper-50">
            {moduleLabel} — {card.display_label}
          </h2>
          <p className="text-body-sm text-ink-300">
            Échoué {card.failure_count} fois
            {failureRel !== null ? ` — dernier ${failureRel}` : ''}
          </p>
          <p className="font-mono text-[11px] text-ink-300">
            Source · <span className="text-paper-50">{card.source_ref}</span>
          </p>
        </div>

        {phase === 'thinking' && (
          <div className="flex flex-col gap-3 p-4 rounded-xl border border-ink-700 bg-ink-800">
            <p className="text-body-sm text-paper-50">
              Prends ton temps. Quand tu penses avoir trouvé, clique pour voir
              la solution.
            </p>
            <div data-testid="reveal-solution-wrap">
              <Button
                variant="primary"
                accent="cyan"
                onClick={() => setPhase('revealed')}
              >
                Voir la solution
              </Button>
            </div>
          </div>
        )}

        {phase === 'revealed' && (
          <>
            <div
              data-testid="solution-panel"
              className="flex flex-col gap-2 p-4 rounded-xl glass glass-volt"
            >
              <Caption color="volt">Solution</Caption>
              {card.expected_move !== null ? (
                <p className="text-body-sm text-paper-50">
                  Bon coup :{' '}
                  <span className="font-mono text-volt-500 text-base">
                    {card.expected_move}
                  </span>
                </p>
              ) : (
                <p className="text-body-sm text-ink-300 italic">
                  Pas de coup unique attendu pour cette carte.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Caption>Comment ça s'est passé ?</Caption>
              <div className="flex flex-col gap-2">
                {OUTCOME_OPTIONS.map((opt) => (
                  <div key={opt.outcome} data-testid={opt.testId}>
                    <Button
                      variant="primary"
                      accent={opt.accent}
                      disabled={isSubmitting}
                      onClick={() => onOutcome(opt.outcome)}
                    >
                      {opt.label}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </aside>
    </section>
  );
}

export default RevisionDrillPage;
